import { AnimatePresence, motion, useReducedMotion } from 'framer-motion'
import { useMemo } from 'react'
import { scatterFrameAt } from '@clicklinx/game-core'
import type { ScatterRound } from '@clicklinx/protocol'
import { ShapeGlyph } from '../components/Shape'
import { useElapsed } from '../lib/useCountdown'
import { PlayField, fieldItemStyle } from './PlayField'

/**
 * Mode 3. The field is replaced wholesale every few seconds and the player
 * keeps a running total of the target across every screenful.
 *
 * Which frame is showing is derived from elapsed time against the server's
 * schedule rather than a chain of local timers, so a dropped frame or a
 * backgrounded tab costs at most one late render instead of desynchronising
 * the sequence - and a reconnecting player lands on the right frame.
 */
export function ScatterStage({
  round,
  startedAtLocal,
  roundKey,
}: {
  round: ScatterRound
  startedAtLocal: number
  /** Stable identity for this round: the server's own start timestamp. */
  roundKey: number
}) {
  const reduceMotion = useReducedMotion()
  /**
   * The round's timing baseline, captured once and then held.
   *
   * `startedAtLocal` derives from the clock offset, which the socket refines
   * every few seconds. Letting that change mid-round would jump the sequence
   * forwards or backwards under the player's eyes.
   */
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const roundStart = useMemo(() => startedAtLocal, [roundKey])

  const elapsed = useElapsed(roundStart, round.displayMs)
  const frame = scatterFrameAt(round, elapsed)

  const frameNumber = (frame?.index ?? 0) + 1
  const withinFrame = frame ? elapsed - frame.startAt : 0
  const frameProgress = frame ? Math.min(1, withinFrame / round.frameMs) : 1

  return (
    <div className="flex min-h-0 w-full flex-1 flex-col gap-3">
      {/* Which screenful this is. Without it, a player who blinks cannot tell
          whether the field changed or they simply mis-saw it. */}
      <div className="flex items-center justify-between px-1">
        <span className="eyebrow">
          Screen {frameNumber} of {round.frames.length}
        </span>
        <span className="font-mono text-xs tnum text-fg-dim">keep the running total</span>
      </div>

      <div className="flex gap-1.5">
        {round.frames.map((f) => (
          <div key={f.index} className="h-1 flex-1 overflow-hidden rounded-full bg-ink-800">
            <div
              className="h-full rounded-full bg-lime transition-[width] duration-100 ease-linear"
              style={{
                width:
                  f.index < (frame?.index ?? round.frames.length)
                    ? '100%'
                    : f.index === frame?.index
                      ? `${frameProgress * 100}%`
                      : '0%',
              }}
            />
          </div>
        ))}
      </div>

      <PlayField>
        {/* Keyed on the frame index so every screenful is a clean swap rather
            than React reconciling one field into the next. */}
        <AnimatePresence mode="wait">
          {frame && (
            <motion.div
              key={frame.index}
              className="absolute inset-0"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: reduceMotion ? 0.01 : 0.12 }}
            >
              {frame.items.map((item, index) => (
                <motion.div
                  key={item.id}
                  className="absolute text-fg"
                  style={fieldItemStyle(item)}
                  initial={reduceMotion ? { opacity: 0 } : { opacity: 0, scale: 0.5 }}
                  animate={reduceMotion ? { opacity: 1 } : { opacity: 1, scale: 1 }}
                  transition={
                    reduceMotion
                      ? { duration: 0.01 }
                      : {
                          type: 'spring',
                          stiffness: 520,
                          damping: 27,
                          // Capped tight: a longer stagger would tell the player
                          // where to look and reward reading the order.
                          delay: Math.min(index * 0.004, 0.1),
                        }
                  }
                >
                  <div
                    className="h-full w-full"
                    style={{ transform: `rotate(${item.rotation}deg)` }}
                  >
                    <ShapeGlyph shape={item.shape} className="h-full w-full" />
                  </div>
                </motion.div>
              ))}
            </motion.div>
          )}
        </AnimatePresence>
      </PlayField>
    </div>
  )
}
